import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { SupportService } from '../shared/support.service';
import { formatSupportCaseNames, FreshdeskAttachment, FreshdeskTicket, SupportDecision, TicketDetail } from '../shared/support.model';

@Component({
  standalone: false,
  selector: 'app-ticket-detail',
  templateUrl: 'ticket-detail.component.html',
})
export class TicketDetailComponent implements OnChanges {
  @Input() ticketId: number;
  readonly formatSupportCaseNames = formatSupportCaseNames;

  loading = false;
  activeTab = 'conversation';
  ticket: FreshdeskTicket | null = null;
  decision: SupportDecision | null = null;
  lightboxUrl: string | null = null;

  constructor(private supportService: SupportService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['ticketId'] && this.ticketId) {
      this.loadTicket();
    }
  }

  loadTicket() {
    this.loading = true;
    this.supportService.getTicketThreadDetail(this.ticketId).subscribe({
      next: (res: TicketDetail) => {
        this.ticket = res.ticket;
        this.decision = res.review?.decision || null;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  getStatusLabel(status: number): string {
    const map = { 2: 'Open', 3: 'Pending', 4: 'Resolved', 5: 'Closed' };
    return map[status] || 'Unknown';
  }

  getStatusClass(status: number): string {
    const map = { 2: 'bg-success', 3: 'bg-warning text-dark', 4: 'bg-info', 5: 'bg-secondary' };
    return map[status] || 'bg-light text-dark';
  }

  getActionTypeClass(type: string): string {
    const map = {
      reply: 'bg-primary',
      close: 'bg-secondary',
      escalate: 'bg-warning text-dark',
      no_action: 'bg-light text-dark',
    };
    return map[type] || 'bg-info';
  }

  getResolvedPhone(decision: SupportDecision | null | undefined): string | null {
    const phones = decision?.promptData?.ticket?.extractedContacts?.phoneNumbers;
    if (Array.isArray(phones) && phones.length > 0 && phones[0].normalized) {
      return phones[0].normalized;
    }
    return null;
  }

  formatConfidence(c: number): string {
    if (typeof c !== 'number' || isNaN(c)) return '—';
    return (c * 100).toFixed(0) + '%';
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  }

  conversationLoadSummary(conversationCount: number | undefined): string {
    const count = typeof conversationCount === 'number' ? conversationCount : 0;

    if (count === 0) {
      return 'No additional conversation messages. Freshdesk embeds at most 10 conversations in ticket detail, so this view loads the full paginated thread on demand.';
    }

    const label = count === 1 ? 'conversation message' : 'conversation messages';
    return `Showing all ${count} ${label}. Freshdesk embeds at most 10 conversations in ticket detail, so this view loads the full paginated thread on demand.`;
  }

  setTab(tab: string) {
    this.activeTab = tab;
  }

  isImageAttachment(att: FreshdeskAttachment): boolean {
    return /^image\//i.test(att.content_type ?? '');
  }

  getImageAttachments(attachments?: FreshdeskAttachment[]): FreshdeskAttachment[] {
    return (attachments ?? []).filter(a => this.isImageAttachment(a) && a.attachment_url);
  }

  openLightbox(url: string) {
    this.lightboxUrl = url;
  }

  closeLightbox() {
    this.lightboxUrl = null;
  }
}
